// ============================================================================
// Book Me Calendar — UI strings (es / en / ca).
//
// Kept in its own module (merged into the main bundles by lib/i18n.js, never
// overwriting an existing key) so the module's strings don't have to be
// threaded through the three large locale JSON files. Only nav.bookme lives in
// those. Spanish is the base; English and Catalan carry exactly the same keys
// (locales/bookme.test.js). Pakistani accounts render English (see Layout).
// ============================================================================

const es = {
  bookme: {
    title: 'Book Me Calendar',
    nav: {
      calendar: 'Calendario',
      import: 'Importar',
      share: 'Enlaces para clientes',
      sites: 'Direcciones',
      tags: 'Etiquetas',
      settings: 'Ajustes',
    },
    status: {
      imported: 'Importada',
      review: 'A revisar',
      available: 'Disponible',
      booked: 'Solicitada',
      assigned: 'Asignada',
      confirmed: 'Confirmada',
      in_progress: 'En curso',
      completed: 'Completada',
      cancelled: 'Cancelada',
    },
    doc: {
      none: 'Sin documento',
      pending: 'Documento pendiente',
      generating: 'Generando documento',
      generated: 'Documento creado',
      failed: 'Error en el documento',
      skipped: 'Sin documento (omitido)',
      title: 'Documento',
      generate: 'Generar documento',
      regenerate: 'Generar de nuevo',
      deleted: 'Documento eliminado',
      deletedHint: 'El documento ya no existe; puedes generarlo de nuevo.',
      invoicedAs: 'Facturado →',
      skippedNote: 'Sin documento (política: ninguno)',
      pendingNote: 'El documento está pendiente de generar.',
      staleHint: 'La generación parece atascada.',
      failedTitle: 'No se ha podido crear el documento',
      readFailed: 'No se ha podido leer el documento.',
      issueHint: 'Se emite desde la vista del documento.',
      type: {
        delivery_note: 'Albarán',
        invoice: 'Factura',
      },
      errors: {
        client_required: 'La reserva necesita un cliente.',
        lines_required: 'La reserva necesita al menos una línea.',
        snapshot_missing: 'Falta la política de documento de la finalización.',
        reserved_id_missing: 'Falta el identificador reservado del documento.',
      },
      invoiceStatus: {
        Draft: 'Borrador',
        Sent: 'Emitido',
        Paid: 'Cobrado',
        Overdue: 'Vencido',
        Converted: 'Convertido',
      },
    },
    actions: {
      new: 'Nueva reserva',
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      duplicate: 'Duplicar',
      delete: 'Eliminar',
      retry: 'Reintentar',
      review: 'Enviar a revisión',
      available: 'Marcar como disponible',
      assigned: 'Asignar',
      confirmed: 'Confirmar',
      in_progress: 'Empezar',
      completed: 'Completar',
      cancelled: 'Cancelar reserva',
      reopen: 'Reabrir',
      approve: 'Aprobar',
      reject: 'Rechazar',
    },
    form: {
      client: 'Cliente',
      service: 'Servicio',
      date: 'Fecha',
      startTime: 'Hora de inicio',
      endTime: 'Hora de fin',
      wholeDay: 'Todo el día',
      duration: 'Duración',
      site: 'Dirección',
      assignee: 'Asignada a',
      status: 'Estado',
      tags: 'Etiquetas',
      internalNotes: 'Notas internas',
      clientNotes: 'Notas para el cliente',
      reason: 'Motivo',
      customerReference: 'Referencia del cliente',
      title: 'Servicio',
      category: 'Categoría',
      performer: 'Ejecutor',
      team: 'Equipo',
      suppliers: 'Proveedores',
      noPerformer: 'Sin asignar',
      newClient: 'Nuevo cliente (se abre Clientes)',
      noSite: 'Sin dirección',
      newSite: 'Nueva dirección…',
      clientSites: 'Direcciones del cliente',
      otherSites: 'Otras direcciones',
      savedSites: 'Direcciones guardadas',
      lines: 'Líneas del servicio',
      description: 'Descripción',
      quantity: 'Cant.',
      unit: 'Unidad',
      unitPrice: 'Precio',
      discount: 'Dto. %',
      taxRate: 'Impuesto %',
      removeLine: 'Quitar línea',
      linesTotal: 'Importe orientativo (sin impuestos)',
      noTags: 'No hay etiquetas.',
      endsAt: 'Termina a las {{time}}',
      newTitle: 'Nueva reserva',
      editTitle: 'Editar reserva #{{ref}}',
      titleRequired: 'Indica el servicio.',
      dateRequired: 'Indica la fecha.',
      durationRequired: 'Indica la duración.',
      durationUnit: 'Unidad de duración',
      durationHours: 'Horas',
      durationDays: 'Días',
      daysInvalid: 'Indica entre 1 y 31 días.',
      seriesEditHint: 'Las reservas de varios días se editan día a día.',
      seriesNote: 'Se creará una reserva de día completo en cada uno de los {{count}} días seguidos.',
      groupCreated: 'Creadas {{count}} reservas',
      groupPartial: 'Creadas {{count}} de {{total}}: {{date}} — {{reason}}',
      siteFields: {
        name: 'Nombre',
        address: 'Dirección',
        number: 'Número',
        postal_code: 'Código postal',
        city: 'Población',
        province: 'Provincia',
        country: 'País',
        contact_name: 'Persona de contacto',
        contact_phone: 'Teléfono de contacto',
        access_instructions: 'Cómo acceder',
        parking_instructions: 'Aparcamiento',
      },
    },
    calendar: {
      wholeDay: 'Todo el día',
      today: 'Hoy',
      loading: 'Cargando reservas…',
      prev: 'Anterior',
      next: 'Siguiente',
      viewLabel: 'Vista',
      pickMonth: 'Mes',
      pickYear: 'Año',
      newOn: 'Nueva reserva el {{date}}',
      views: { month: 'Mes', week: 'Semana', day: 'Día', agenda: 'Agenda' },
      more: '+{{count}} más',
      noBookings: 'No hay reservas.',
      noClient: 'Sin cliente',
      deletedClient: 'Cliente eliminado',
      deletedPerformer: 'Ejecutor eliminado',
      deletedSite: 'Dirección eliminada',
      deletedProduct: 'Producto eliminado',
      reviewPending_one: '{{count}} reserva pendiente de revisión',
      reviewPending_other: '{{count}} reservas pendientes de revisión',
      filters: {
        title: 'Filtros',
        text: 'Buscar',
        textPlaceholder: 'Buscar por nº, servicio o cliente',
        performer: 'Ejecutor',
        any: 'Todos',
        outOfRange: 'Seleccionado (fuera de estas fechas)',
        document: 'Documento',
        clear: 'Quitar filtros',
      },
    },
    conflict: {
      alternative: 'Primera hora libre ese día: {{time}}.',
      noAlternative: 'No queda ninguna hora libre ese día.',
      useAlternative: 'Usar las {{time}}',
      clashRefs: 'Coincide con: {{refs}}',
      force: 'Forzar',
    },
    detail: {
      notFound: 'No se ha encontrado la reserva.',
      back: 'Volver al calendario',
      untitled: 'Reserva sin título',
      when: 'Fecha y hora',
      noDate: 'Sin fecha',
      minutes_one: '{{count}} minuto',
      minutes_other: '{{count}} minutos',
      tzHint: 'Horas en la zona {{tz}}.',
      groupSeries: 'Forma parte de una serie de {{count}} días',
      groupOpen: 'Ver en el calendario',
      whatsapp: 'WhatsApp',
      directions: 'Cómo llegar',
      noLines: 'Sin líneas.',
      assign: 'Asignar ejecutor',
      assignTitle: 'Asignar ejecutor',
      notes: 'Notas',
      timeline: 'Historial y comentarios',
      commentPlaceholder: 'Escribe un comentario…',
      sendComment: 'Comentar',
      noEvents: 'Todavía no hay historial.',
      shareWithClient: 'Compartir con el cliente',
      duplicateTitle: 'Duplicar reserva',
      duplicateDates: 'En fechas concretas',
      duplicateWeekly: 'Cada semana',
      addDate: 'Añadir fecha',
      weeks: 'Número de semanas (incluida esta)',
      weeksHint: 'Entre 2 y 52; las copias empiezan la semana siguiente.',
      duplicated_one: '{{count}} copia creada',
      duplicated_other: '{{count}} copias creadas',
      duplicateSkipped: 'No se han podido crear: {{dates}}',
    },
    events: {
      created: 'Reserva creada',
      updated: 'Datos modificados',
      rescheduled: 'Cambio de fecha u hora',
      assigned: 'Asignación',
      status: 'Cambio de estado',
      tagged: 'Etiquetas cambiadas',
      comment: 'Comentario',
      completed: 'Servicio completado',
      document_generated: 'Documento creado',
      document_failed: 'Error al crear el documento',
      cancelled: 'Reserva cancelada',
      reopened: 'Reserva reabierta',
      public_booked: 'Solicitada por el cliente',
      document_viewed: 'El cliente ha visto el documento',
      document_regenerate: 'Documento regenerado',
      approved: 'Solicitud aprobada',
      rejected: 'Solicitud rechazada',
      import_updated: 'Actualizada por una importación',
      import_cancelled: 'Cancelada por una importación',
      attachment_added: 'Adjunto añadido',
      attachment_removed: 'Adjunto eliminado',
      other: 'Cambio',
      forced: '(forzado)',
    },
    import: {
      title: 'Importar reservas',
      history: 'Historial',
      historyTitle: 'Importaciones anteriores',
      intro: 'Pega mensajes de WhatsApp, correos o listas, o sube un archivo. Nada se convierte en reserva hasta que lo confirmes.',
      steps: { source: 'Origen', preview: 'Revisión', report: 'Resultado' },
      paste: 'Texto pegado',
      pastePlaceholder: 'Pega aquí los mensajes o la lista de servicios…',
      file: 'Archivo',
      fileHint: '.txt (chat de WhatsApp exportado), .csv, .xlsx o .ics (invitaciones de calendario). El archivo se lee en tu navegador.',
      fileChosen: 'Archivo: {{name}}',
      fileWins: 'Se analizará el archivo; el texto pegado se ignora mientras haya uno elegido.',
      removeFile: 'Quitar archivo',
      ai: 'Asistente IA',
      aiHint: 'Completa la fecha, la dirección o el servicio que falten con las palabras del propio mensaje. Solo se aplica si la plataforma lo tiene activado.',
      analyze: 'Analizar',
      emptyFile: 'El archivo no tiene filas.',
      unreadableFile: 'No se ha podido leer el archivo.',
      unsupportedFile: 'Tipo de archivo no admitido. Usa .txt, .csv, .xlsx o .ics.',
      rowsFound_one: '{{count}} fila analizada',
      rowsFound_other: '{{count}} filas analizadas',
      states: { ok: 'Correcta', warn: 'Con avisos', blocked: 'Bloqueada', dup: 'Duplicada', series: 'Serie' },
      aiBudgetExhausted: 'El asistente IA no llegó a todas las filas.',
      options: {
        title: 'Opciones',
        create_clients: 'Crear los clientes que no existan',
        create_sites: 'Crear las direcciones que no existan',
        default_status: 'Estado de las reservas importadas',
        import_duplicates: 'Importar también los duplicados',
        hint: 'Una fila con un aviso bloqueante se importa siempre como «A revisar».',
      },
      commit_one: 'Importar {{count}} fila',
      commit_other: 'Importar {{count}} filas',
      skippedCount_one: '({{count}} omitida)',
      skippedCount_other: '({{count}} omitidas)',
      committing: 'Importando {{done}} de {{total}}…',
      startOver: 'Empezar de nuevo',
      columns: {
        row: 'Nº',
        state: 'Estado',
        decision: 'Decisión',
        service_date: 'Fecha',
        start_time: 'Inicio',
        end_time: 'Fin',
        client_name: 'Cliente',
        phone: 'Teléfono',
        address: 'Dirección',
        postal_code: 'C. postal',
        city: 'Población',
        service: 'Servicio',
        title: 'Título',
        notes: 'Notas',
        tags: 'Etiquetas',
        issues: 'Avisos',
        outcome: 'Resultado',
      },
      seriesNotImported: 'Serie recurrente — no se importa automáticamente',
      seriesHint: 'La serie completa no se importa; créala a mano o importa solo su primera fecha.',
      importFirstOnly: 'Importar solo la primera fecha',
      duplicateOf: 'Duplicado de una reserva existente',
      duplicateInFile: 'Repetida en esta importación',
      openBooking: 'Ver reserva',
      rawText: 'Texto original',
      editCell: 'Editar',
      pickClient: 'Elegir de mis clientes',
      unlinkClient: 'Quitar el cliente elegido',
      linkedClient: 'Cliente vinculado: {{name}}',
      closePicker: 'Cerrar',
      provenance: {
        label: 'Procedencia',
        from: 'Leído de: «{{text}}»',
        original: 'Original: {{value}}',
        edited: 'Editado a mano',
        none: 'No aparece en el texto original',
      },
      confidence: { high: 'Lectura segura', low: 'Lectura dudosa', ai: 'Sugerido por el asistente IA' },
      issues: {
        missing_date: 'Falta la fecha',
        ambiguous_date: 'Fecha ambigua',
        invalid_date: 'Fecha no válida',
        invalid_local_time: 'Esa hora no existe ese día (cambio de hora)',
        past_date: 'Fecha pasada',
        missing_service: 'Falta el servicio',
        missing_client: 'Falta el cliente',
        unknown_client: 'Cliente no encontrado en tus contactos',
        missing_address: 'Falta la dirección',
        duplicate: 'Duplicada',
        recurring_not_expanded: 'Serie recurrente sin expandir',
        ics_cancel: 'Evento cancelado en el calendario de origen',
        ics_cancel_unknown: 'Cancela un evento que no se había importado',
        ics_update: 'Actualiza una reserva importada antes',
        ics_no_uid: 'Evento sin identificador (UID)',
        ics_override: 'Cambio de una fecha concreta de una serie',
        multi_day: 'Dura varios días',
        crosses_midnight: 'Termina pasada la medianoche',
        dst_adjusted: 'Hora ajustada por el cambio de hora',
        invalid_event: 'Evento ilegible',
        empty_row: 'Fila vacía',
        ai_suggested_service_date: 'Fecha sugerida por el asistente IA',
        ai_suggested_start_time: 'Hora sugerida por el asistente IA',
        ai_suggested_address: 'Dirección sugerida por el asistente IA',
        ai_suggested_service: 'Servicio sugerido por el asistente IA',
        ai_suggested_client_name: 'Cliente sugerido por el asistente IA',
        other: 'Aviso',
      },
      decisions: { import: 'Importar', skip: 'Omitir', update: 'Actualizar la reserva', cancel: 'Cancelar la reserva', pending: 'Sin decidir' },
      outcomes: { imported: 'Importada', skipped: 'Omitida', updated: 'Actualizada', cancelled: 'Cancelada', failed: 'Con error' },
      report: {
        title: 'Resultado de la importación',
        imported: 'Importadas',
        updated: 'Actualizadas',
        cancelled: 'Canceladas',
        skipped: 'Omitidas',
        failed: 'Con error',
        review: 'Por revisar',
        series_pending: 'Series pendientes de crear a mano',
        pending: 'Sin confirmar',
        rows: 'Detalle por fila',
        viewCalendar: 'Ver calendario',
        newImport: 'Nueva importación',
      },
      sources: { paste: 'Texto pegado', txt: 'Archivo de texto', csv: 'CSV', xlsx: 'Excel', ics: 'Calendario (ICS)' },
      statuses: { parsed: 'Por confirmar', committed: 'Confirmada', cancelled: 'Cancelada' },
      historyEmpty: 'Todavía no hay importaciones.',
      historyColumns: { date: 'Fecha', source: 'Origen', file: 'Archivo', rows: 'Filas', status: 'Estado', result: 'Resultado' },
      open: 'Abrir',
      notFound: 'No se ha encontrado la importación.',
      backToHistory: 'Volver al historial',
      readOnly: 'Importación confirmada: las filas se muestran tal como quedaron.',
    },
    share: {
      title: 'Enlaces para clientes',
      intro: 'Cada enlace abre un portal donde el cliente reserva y ve sus servicios. Solo se guarda una huella del enlace: la dirección completa se muestra una única vez.',
      new: 'Nuevo enlace',
      newTitle: 'Nuevo enlace para un cliente',
      editTitle: 'Editar enlace',
      hint: 'El cliente se elige entre tus contactos; aquí no se modifica.',
      empty: 'Todavía no hay enlaces.',
      filterClient: 'Cliente',
      allClients: 'Todos los clientes',
      unnamed: 'Enlace sin nombre',
      created: 'Creado el {{date}}',
      uses_one: '{{count}} reserva de {{max}}',
      uses_other: '{{count}} reservas de {{max}}',
      usesUnlimited_one: '{{count}} reserva',
      usesUnlimited_other: '{{count}} reservas',
      state: { active: 'Activo', expired: 'Caducado', revoked: 'Revocado' },
      columns: { name: 'Enlace', prefix: 'Código', state: 'Estado', uses: 'Uso', created: 'Creado' },
      fields: {
        client: 'Cliente',
        name: 'Nombre del enlace',
        allowBooking: 'Permitir reservas',
        allowBookingHint: 'Si no, el cliente solo ve sus servicios.',
        autoConfirm: 'Confirmar automáticamente',
        autoConfirmHint: 'Las reservas entran confirmadas, sin tu aprobación.',
        allowHolidays: 'Permitir reservar en festivos',
        allowHolidaysHint: 'Si se desactiva, el cliente no puede elegir días festivos (nacionales y de Cataluña).',
        expires: 'Caduca el',
        maxBookings: 'Máximo de reservas activas',
        daysAhead: 'Días que se pueden reservar por adelantado',
        slotMinutes: 'Tramos de reserva',
        slotDefault: 'Como en los ajustes',
        wholeDay: 'Días completos',
        dayCapacity: 'Reservas por tramo',
        dayCapacityHint: 'Vacío = como en los ajustes.',
        services: 'Servicios que puede elegir',
        servicesHint: 'Si eliges alguno, el cliente tendrá que escoger uno al reservar.',
        servicesSearch: 'Buscar servicio',
        tags: 'Etiquetas visibles para el cliente',
        tagsHint: 'Solo las etiquetas marcadas como visibles para el cliente.',
        message: 'Mensaje de bienvenida',
        lang: 'Idioma del portal',
      },
      minutes: '{{count}} min',
      noProducts: 'No hay productos en tu catálogo.',
      noTags: 'No hay etiquetas visibles para el cliente.',
      langs: { es: 'Español', en: 'English', ca: 'Català' },
      problems: {
        clientRequired: 'Elige el cliente.',
        daysAhead: 'Los días por adelantado deben estar entre 1 y 365.',
        number: 'Revisa los números: deben ser enteros de 1 en adelante.',
        message: 'El mensaje admite como máximo 500 caracteres.',
      },
      saved: 'Enlace guardado.',
      revoke: 'Revocar',
      revokeTitle: 'Revocar enlace',
      revokeText: 'El enlace dejará de funcionar al momento. Las reservas hechas se conservan.',
      revoked: 'Enlace revocado.',
      regenerate: 'Generar nuevo enlace',
      regenerateTitle: 'Generar un enlace nuevo',
      regenerateText: 'La dirección anterior dejará de funcionar y se creará otra para el mismo cliente.',
      tokenTitle: 'Enlace listo',
      tokenOnce: 'Copia o comparte el enlace ahora: por seguridad no se volverá a mostrar.',
      copy: 'Copiar',
      copied: 'Enlace copiado.',
      share: 'Compartir',
      whatsapp: 'Enviar por WhatsApp',
      shareTitle: 'Tu portal de reservas',
      shareText: 'Hola {{name}}, desde este enlace puedes reservar y ver tus servicios:',
      done: 'Hecho',
    },
    portal: {
      title: 'Tus reservas',
      loading: 'Cargando…',
      linkInactive: 'Este enlace ya no está activo',
      linkInactiveHint: 'Pide un enlace nuevo a la empresa.',
      tooManyRequests: 'Demasiadas solicitudes. Espera unos minutos y vuelve a intentarlo.',
      loadFailed: 'No se ha podido cargar el portal. Revisa tu conexión.',
      greeting: 'Hola, {{name}}',
      language: 'Idioma',
      tabs: { book: 'Reservar', services: 'Mis servicios' },
      bookingClosed: 'Este enlace no admite nuevas reservas.',
      bookingsLeft_one: 'Puedes hacer {{count}} reserva más con este enlace.',
      bookingsLeft_other: 'Puedes hacer {{count}} reservas más con este enlace.',
      date: 'Fecha',
      noDates: 'No hay días libres en este periodo.',
      full: 'Completo — no disponible',
      legend: { free: 'Disponible', partial: 'Parcialmente reservado', full: 'Completo (no disponible)' },
      days: 'Días',
      typeDays: 'Escribe los días (p. ej. 1 5 7)',
      addDays: 'Añadir',
      allMonth: 'Añadir todos los días libres del mes',
      selectedCount_one: '{{count}} día elegido',
      selectedCount_other: '{{count}} días elegidos',
      removeDay: 'Quitar {{date}}',
      bookedCount_one: 'Reservado {{count}} día',
      bookedCount_other: 'Reservados {{count}} días',
      skippedNote_one: '{{count}} día no disponible:',
      skippedNote_other: '{{count}} días no disponibles:',
      noneBooked: 'No se ha podido reservar ningún día de los elegidos.',
      prevMonth: 'Mes anterior',
      nextMonth: 'Mes siguiente',
      time: 'Hora',
      noSlots: 'No quedan horas libres ese día.',
      wholeDay: 'Día completo',
      service: 'Servicio',
      pickService: 'Elige un servicio',
      tags: 'Etiquetas',
      what: 'Qué necesitas',
      description: 'Descripción',
      notes: 'Notas para la empresa',
      site: 'Dirección del servicio',
      review: 'Revisa tu reserva',
      continue: 'Revisar',
      edit: 'Editar',
      send: 'Enviar reserva',
      sending: 'Enviando…',
      captcha: 'Confirma que no eres un robot.',
      newTag: 'Nueva etiqueta',
      newTags: 'Etiquetas nuevas',
      addTag: 'Añadir',
      removeTag: 'Quitar {{name}}',
      newTagHint: 'Hasta {{max}} etiquetas de {{len}} caracteres como máximo.',
      problems: {
        date: 'Elige un día.',
        time: 'Elige una hora.',
        service: 'Elige un servicio.',
        title: 'Indica qué necesitas.',
        captcha: 'Marca la casilla «No soy un robot».',
      },
      slotFullAlternative: 'Hay hueco a las {{time}}.',
      slotFullNone: 'Vuelve atrás y elige otra hora.',
      pickAlternative: 'Reservar a las {{time}}',
      sent: 'Reserva enviada',
      reference: 'Referencia',
      pendingNote: 'La empresa revisará tu solicitud y te confirmará la reserva.',
      confirmedNote: 'Tu reserva está confirmada.',
      another: 'Hacer otra reserva',
      upcoming: 'Próximos',
      past: 'Anteriores',
      noBookings: 'Todavía no tienes servicios.',
      completionNotes: 'Notas del servicio',
      report: 'Ver informe de entrega',
      reportLoading: 'Cargando informe…',
      documentUnavailable: 'El informe no está disponible.',
      download: 'Descargar PDF',
      downloadFailed: 'No se ha podido crear el PDF.',
      previewFailed: 'No se ha podido mostrar la vista previa; puedes descargar el PDF.',
      pageAlt: 'Página {{n}} de {{total}}',
      back: 'Volver a mis servicios',
    },
    sites: {
      title: 'Sitios',
      new: 'Nuevo sitio',
      newTitle: 'Nuevo sitio',
      editTitle: 'Editar sitio',
      hint: 'Dirección donde se hace el servicio. El cliente se elige entre tus contactos; aquí no se modifica.',
      search: 'Buscar sitio, dirección o cliente',
      empty: 'Todavía no hay sitios.',
      name: 'Nombre',
      client: 'Cliente',
      address: 'Dirección',
      city: 'Ciudad',
      contact: 'Contacto',
      nameRequired: 'Indica el nombre del sitio.',
      saved: 'Sitio guardado.',
      archive: 'Archivar',
      archiveTitle: 'Archivar sitio',
      archiveText: '«{{name}}» dejará de ofrecerse al crear reservas. Las reservas existentes lo conservan.',
      archived: 'Sitio archivado.',
      fields: {
        name: 'Nombre',
        address: 'Dirección',
        number: 'Número',
        postal_code: 'Código postal',
        city: 'Ciudad',
        province: 'Provincia',
        country: 'País',
        contact_name: 'Persona de contacto',
        contact_phone: 'Teléfono de contacto',
        lat: 'Latitud',
        lng: 'Longitud',
        access_instructions: 'Cómo acceder',
        parking_instructions: 'Aparcamiento',
        notes: 'Notas',
      },
    },
    tags: {
      title: 'Etiquetas',
      new: 'Nueva etiqueta',
      newTitle: 'Nueva etiqueta',
      editTitle: 'Editar etiqueta',
      hint: 'Las etiquetas visibles se muestran al cliente; las seleccionables las puede elegir él.',
      empty: 'Todavía no hay etiquetas.',
      name: 'Nombre',
      color: 'Color',
      clientVisible: 'Visible para el cliente',
      clientSelectable: 'El cliente puede elegirla',
      sourceLabel: 'Origen',
      source: { internal: 'Interna', client: 'Del cliente' },
      yes: 'Sí',
      no: 'No',
      nameRequired: 'Indica el nombre de la etiqueta.',
      saved: 'Etiqueta guardada.',
      archive: 'Archivar',
      archiveTitle: 'Archivar etiqueta',
      archiveText: '«{{name}}» dejará de ofrecerse. Las reservas que ya la tienen la conservan.',
      archived: 'Etiqueta archivada.',
    },
    settings: {
      title: 'Ajustes de Book Me',
      saved: 'Ajustes guardados.',
      nothingToSave: 'No hay cambios que guardar.',
      hours: 'Horario',
      timezone: 'Zona horaria',
      slotMinutes: 'Tamaño de franja',
      dayStart: 'Hora de apertura',
      dayEnd: 'Hora de cierre',
      rangeInvalid: 'La hora de cierre debe ser posterior a la de apertura.',
      dayCapacity: 'Reservas por franja o día',
      defaultDuration: 'Duración por defecto (min)',
      documents: 'Documentos',
      docType: 'Documento al completar',
      docMode: 'Generación',
      docStatus: 'Estado de la factura',
      docTypes: { none: 'Ninguno', delivery_note: 'Albarán', invoice: 'Factura' },
      docModes: { auto: 'Automática', manual: 'Manual' },
      docStatuses: { Draft: 'Borrador', Sent: 'Enviada' },
      taxRate: 'IVA por defecto (%)',
      irpfRate: 'IRPF (%)',
      portal: 'Portal del cliente',
      requiresApproval: 'Las solicitudes del cliente requieren aprobación',
      requiresApprovalHelp: 'Valor por defecto de los nuevos enlaces; cada enlace puede cambiarlo',
      shareDocuments: 'Compartir documentos con el cliente',
      clientTags: 'El cliente puede crear etiquetas',
      notices: 'Avisos',
      notifyEmail: 'Avisarme por correo',
    },
    complete: {
      title: 'Completar servicio',
      actualAt: 'Fecha y hora reales',
      duration: 'Duración real (min)',
      notes: 'Notas de finalización',
      reviewLines: 'Revisar líneas',
      hideLines: 'Ocultar líneas',
      linesHint: 'Los cambios solo afectan a esta reserva y al documento que se cree.',
      submit: 'Completar',
      policy: {
        delivery_note_auto: 'Se creará un albarán en borrador',
        invoice_auto: 'Se creará una factura en borrador',
        none: 'No se creará ningún documento',
        manual: 'El documento quedará pendiente de generar a mano',
      },
      toast: {
        generated: 'Servicio completado y documento creado',
        pending: 'Servicio completado; el documento queda pendiente de generar',
        skipped: 'Servicio completado; no se crea documento',
        completed: 'Servicio completado',
      },
    },
    attachments: {
      title: 'Adjuntos',
      note: 'Los adjuntos son internos: el cliente nunca los ve.',
      upload: 'Subir archivo',
      uploading: 'Subiendo…',
      empty: 'Sin adjuntos.',
      delete: 'Eliminar adjunto',
      confirmDelete: '¿Eliminar este adjunto?',
      badType: 'Solo se admiten imágenes y PDF.',
      uploadFailed: 'No se ha podido subir el archivo.',
    },
    notifications: {
      title: 'Avisos',
      aria: 'Avisos ({{count}} sin leer)',
      none: 'No hay avisos.',
      markRead: 'Marcar leídas',
    },
    errors: {
      document_exists: 'El documento de esta reserva ya existe o se está creando.',
      not_completed: 'El servicio todavía no está completado.',
      client_required: 'La reserva necesita un cliente para crear el documento.',
      lines_required: 'La reserva necesita al menos una línea para crear el documento.',
      snapshot_missing: 'Falta la política de documento de la finalización.',
      reserved_id_missing: 'Falta el identificador reservado del documento.',
      invalid_path: 'La ruta del archivo no es válida.',
      slot_full: 'Ese horario ya está completo.',
      performer_clash: 'La persona asignada ya tiene otro servicio a esa hora.',
      link_limit: 'Este enlace ya ha alcanzado su límite de reservas.',
      invalid_client: 'Cliente no válido.',
      invalid_vendor: 'Proveedor no válido.',
      invalid_assignee: 'La persona asignada no forma parte de tu equipo.',
      invalid_product: 'Producto no válido.',
      invalid_site: 'Dirección no válida.',
      invalid_tag: 'Etiqueta no válida.',
      invalid_time: 'Revisa la fecha y las horas.',
      invalid_days: 'Los días seguidos solo valen para una reserva de día completo con fecha (de 2 a 31).',
      invalid_local_time: 'Esa hora no existe ese día por el cambio de hora.',
      invalid_status: 'Estado no válido.',
      invalid_transition: 'La reserva no puede pasar a ese estado desde el actual.',
      reason_required: 'Indica el motivo.',
      use_complete_endpoint: 'Usa «Completar» para cerrar el servicio.',
      settings_missing: 'Guarda primero los ajustes de Book Me.',
      feature_off: 'Book Me no está activado en esta cuenta.',
      forbidden: 'No tienes permiso para hacer esto.',
      not_found: 'No se ha encontrado.',
      range_too_long: 'El periodo de fechas es demasiado largo.',
      invalid_timezone: 'Zona horaria no válida.',
      invalid_value: 'Revisa los datos introducidos.',
      use_status_endpoint: 'El estado se cambia desde su propio botón.',
      booking_completed: 'Una reserva completada no se puede mover ni reasignar.',
      duplicate_failed: 'No se ha podido crear ninguna copia.',
      tag_exists: 'Ya existe una etiqueta con ese nombre.',
      db_error: 'No se ha podido guardar. Inténtalo de nuevo.',
      nothing_to_import: 'No se ha encontrado nada que importar.',
      import_closed: 'Esta fila ya está importada o la importación está cerrada.',
      too_many_rows: 'Demasiadas filas en una sola confirmación.',
      too_large: 'El texto o el archivo es demasiado grande (máximo 500.000 caracteres o 2.000 filas).',
      invalid_decision: 'Esa decisión no es válida para esta fila.',
      invalid_date: 'Fecha no válida.',
      internal: 'Error inesperado en esta fila.',
      duplicate: 'Ya estaba importada (duplicado).',
      already_cancelled: 'La reserva ya estaba cancelada.',
      pending: 'Fila sin decidir.',
      loadFailed: 'No se han podido cargar los datos.',
      link_invalid: 'Este enlace ya no está activo.',
      past_date: 'Esa fecha u hora ya ha pasado.',
      outside_window: 'Esa fecha está fuera del periodo de reservas.',
      tags_not_allowed: 'No se pueden crear etiquetas nuevas.',
      captcha_failed: 'No se ha podido verificar que no eres un robot. Inténtalo de nuevo.',
      booking_disabled: 'Este enlace no admite reservas.',
      holiday: 'Ese día es festivo; elige otro.',
      rate_limited: 'Demasiadas solicitudes. Espera unos minutos.',
      stale_status: 'La reserva cambió mientras tanto; recarga la página',
      unknown: 'Algo ha fallado. Inténtalo de nuevo.',
    },
    billing: {
      title: 'Facturación por horas',
      assignedIndicator: 'Se factura por horas (OND)',
      viewInvoice: 'Ver factura OND',
      schemes: 'Esquemas de facturación',
      period: 'Periodo',
      format: 'Formato',
      formatDay: 'Una línea por día',
      formatMonth: 'Una línea para todo el mes',
      preview: 'Vista previa',
      create: 'Crear factura',
      release: 'Liberar reservas',
      noBookings: 'No hay reservas por facturar en este periodo',
      hours: 'Horas',
      rate: 'Tarifa por hora',
      days: 'Días',
      hoursTotal: 'Total de horas',
      daysTotal: 'Total de días',
      includePending: 'Incluir reservas pendientes',
      created: 'Factura creada',
      invoiceNumber: 'Nº de factura',
      schemeTitle: 'Esquema de facturación',
      addScheme: 'Añadir esquema',
      product: 'Producto',
      productHint: 'Producto o servicio que se factura por hora',
      hoursPerDay: 'Horas por día',
      defaultFormat: 'Formato por defecto',
      perBookingDocument: 'Un documento por reserva',
      seriesPrefix: 'Prefijo de serie',
    },
    ond: {
      period: 'Periodo OND',
      totalHours: 'Horas totales',
      totalDays: 'Días totales',
    },
  },
  perms: {
    mod_bookme: 'Book Me Calendar',
    'p_bookme.view': 'Ver calendario',
    'p_bookme.manage': 'Crear y editar reservas',
    'p_bookme.import': 'Importar reservas',
    'p_bookme.assign': 'Asignar ejecutor',
    'p_bookme.complete': 'Completar servicios',
    'p_bookme.documents': 'Generar albarán / factura',
    'p_bookme.share': 'Enlaces para clientes',
    'p_bookme.settings': 'Ajustes de Book Me',
  },
};

const en = {
  bookme: {
    title: 'Book Me Calendar',
    nav: {
      calendar: 'Calendar',
      import: 'Import',
      share: 'Client links',
      sites: 'Addresses',
      tags: 'Tags',
      settings: 'Settings',
    },
    status: {
      imported: 'Imported',
      review: 'To review',
      available: 'Available',
      booked: 'Requested',
      assigned: 'Assigned',
      confirmed: 'Confirmed',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    doc: {
      none: 'No document',
      pending: 'Document pending',
      generating: 'Generating document',
      generated: 'Document created',
      failed: 'Document error',
      skipped: 'No document (skipped)',
      title: 'Document',
      generate: 'Generate document',
      regenerate: 'Generate again',
      deleted: 'Document deleted',
      deletedHint: 'The document no longer exists; you can generate it again.',
      invoicedAs: 'Invoiced →',
      skippedNote: 'No document (policy: none)',
      pendingNote: 'The document is waiting to be generated.',
      staleHint: 'Generation looks stuck.',
      failedTitle: 'The document could not be created',
      readFailed: 'The document could not be read.',
      issueHint: 'It is issued from the document view.',
      type: {
        delivery_note: 'Delivery note',
        invoice: 'Invoice',
      },
      errors: {
        client_required: 'The booking needs a client.',
        lines_required: 'The booking needs at least one line.',
        snapshot_missing: 'The completion has no document policy.',
        reserved_id_missing: 'The reserved document id is missing.',
      },
      invoiceStatus: {
        Draft: 'Draft',
        Sent: 'Issued',
        Paid: 'Paid',
        Overdue: 'Overdue',
        Converted: 'Converted',
      },
    },
    actions: {
      new: 'New booking',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      duplicate: 'Duplicate',
      delete: 'Delete',
      retry: 'Retry',
      review: 'Send to review',
      available: 'Mark as available',
      assigned: 'Assign',
      confirmed: 'Confirm',
      in_progress: 'Start',
      completed: 'Complete',
      cancelled: 'Cancel booking',
      reopen: 'Reopen',
      approve: 'Approve',
      reject: 'Reject',
    },
    form: {
      client: 'Client',
      service: 'Service',
      date: 'Date',
      startTime: 'Start time',
      endTime: 'End time',
      wholeDay: 'All day',
      duration: 'Duration',
      site: 'Address',
      assignee: 'Assigned to',
      status: 'Status',
      tags: 'Tags',
      internalNotes: 'Internal notes',
      clientNotes: 'Notes for the client',
      reason: 'Reason',
      customerReference: 'Client reference',
      title: 'Service',
      category: 'Category',
      performer: 'Performer',
      team: 'Team',
      suppliers: 'Suppliers',
      noPerformer: 'Unassigned',
      newClient: 'New client (opens Clients)',
      noSite: 'No address',
      newSite: 'New address…',
      clientSites: 'Client\'s addresses',
      otherSites: 'Other addresses',
      savedSites: 'Saved addresses',
      lines: 'Service lines',
      description: 'Description',
      quantity: 'Qty',
      unit: 'Unit',
      unitPrice: 'Price',
      discount: 'Disc. %',
      taxRate: 'Tax %',
      removeLine: 'Remove line',
      linesTotal: 'Indicative amount (before tax)',
      noTags: 'No tags yet.',
      endsAt: 'Ends at {{time}}',
      newTitle: 'New booking',
      editTitle: 'Edit booking #{{ref}}',
      titleRequired: 'Enter the service.',
      dateRequired: 'Enter the date.',
      durationRequired: 'Enter the duration.',
      durationUnit: 'Duration unit',
      durationHours: 'Hours',
      durationDays: 'Days',
      daysInvalid: 'Enter between 1 and 31 days.',
      seriesEditHint: 'Multi-day bookings are edited day by day.',
      seriesNote: 'One whole-day booking will be created on each of the {{count}} consecutive days.',
      groupCreated: 'Created {{count}} bookings',
      groupPartial: 'Created {{count}} of {{total}}: {{date}} — {{reason}}',
      siteFields: {
        name: 'Name',
        address: 'Address',
        number: 'Number',
        postal_code: 'Postcode',
        city: 'Town / city',
        province: 'Province',
        country: 'Country',
        contact_name: 'Contact person',
        contact_phone: 'Contact phone',
        access_instructions: 'How to get in',
        parking_instructions: 'Parking',
      },
    },
    calendar: {
      wholeDay: 'All day',
      today: 'Today',
      loading: 'Loading bookings…',
      prev: 'Previous',
      next: 'Next',
      viewLabel: 'View',
      pickMonth: 'Month',
      pickYear: 'Year',
      newOn: 'New booking on {{date}}',
      views: { month: 'Month', week: 'Week', day: 'Day', agenda: 'Agenda' },
      more: '+{{count}} more',
      noBookings: 'No bookings.',
      noClient: 'No client',
      deletedClient: 'Deleted client',
      deletedPerformer: 'Deleted performer',
      deletedSite: 'Deleted address',
      deletedProduct: 'Deleted product',
      reviewPending_one: '{{count}} booking waiting for review',
      reviewPending_other: '{{count}} bookings waiting for review',
      filters: {
        title: 'Filters',
        text: 'Search',
        textPlaceholder: 'Search by no., service or client',
        performer: 'Performer',
        any: 'All',
        outOfRange: 'Selected (outside these dates)',
        document: 'Document',
        clear: 'Clear filters',
      },
    },
    conflict: {
      alternative: 'First free time that day: {{time}}.',
      noAlternative: 'There is no free time left that day.',
      useAlternative: 'Use {{time}}',
      clashRefs: 'Clashes with: {{refs}}',
      force: 'Force',
    },
    detail: {
      notFound: 'Booking not found.',
      back: 'Back to the calendar',
      untitled: 'Untitled booking',
      when: 'Date and time',
      noDate: 'No date',
      minutes_one: '{{count}} minute',
      minutes_other: '{{count}} minutes',
      tzHint: 'Times in the {{tz}} zone.',
      groupSeries: 'Part of a series of {{count}} days',
      groupOpen: 'View in the calendar',
      whatsapp: 'WhatsApp',
      directions: 'Directions',
      noLines: 'No lines.',
      assign: 'Assign performer',
      assignTitle: 'Assign performer',
      notes: 'Notes',
      timeline: 'History and comments',
      commentPlaceholder: 'Write a comment…',
      sendComment: 'Comment',
      noEvents: 'No history yet.',
      shareWithClient: 'Share with the client',
      duplicateTitle: 'Duplicate booking',
      duplicateDates: 'On specific dates',
      duplicateWeekly: 'Every week',
      addDate: 'Add date',
      weeks: 'Number of weeks (this one included)',
      weeksHint: 'Between 2 and 52; the copies start next week.',
      duplicated_one: '{{count}} copy created',
      duplicated_other: '{{count}} copies created',
      duplicateSkipped: 'Could not be created: {{dates}}',
    },
    events: {
      created: 'Booking created',
      updated: 'Details changed',
      rescheduled: 'Date or time changed',
      assigned: 'Assignment',
      status: 'Status changed',
      tagged: 'Tags changed',
      comment: 'Comment',
      completed: 'Job completed',
      document_generated: 'Document created',
      document_failed: 'Document creation failed',
      cancelled: 'Booking cancelled',
      reopened: 'Booking reopened',
      public_booked: 'Requested by the client',
      document_viewed: 'The client viewed the document',
      document_regenerate: 'Document regenerated',
      approved: 'Request approved',
      rejected: 'Request rejected',
      import_updated: 'Updated by an import',
      import_cancelled: 'Cancelled by an import',
      attachment_added: 'Attachment added',
      attachment_removed: 'Attachment removed',
      other: 'Change',
      forced: '(forced)',
    },
    import: {
      title: 'Import bookings',
      history: 'History',
      historyTitle: 'Previous imports',
      intro: 'Paste WhatsApp messages, e-mails or lists, or upload a file. Nothing becomes a booking until you confirm it.',
      steps: { source: 'Source', preview: 'Review', report: 'Result' },
      paste: 'Pasted text',
      pastePlaceholder: 'Paste the messages or the list of services here…',
      file: 'File',
      fileHint: '.txt (exported WhatsApp chat), .csv, .xlsx or .ics (calendar invitations). The file is read in your browser.',
      fileChosen: 'File: {{name}}',
      fileWins: 'The file will be analysed; the pasted text is ignored while a file is chosen.',
      removeFile: 'Remove file',
      ai: 'AI assistant',
      aiHint: 'Fills a missing date, address or service with the message\'s own words. Only applies when the platform has it enabled.',
      analyze: 'Analyse',
      emptyFile: 'The file has no rows.',
      unreadableFile: 'The file could not be read.',
      unsupportedFile: 'Unsupported file type. Use .txt, .csv, .xlsx or .ics.',
      rowsFound_one: '{{count}} row analysed',
      rowsFound_other: '{{count}} rows analysed',
      states: { ok: 'OK', warn: 'With warnings', blocked: 'Blocked', dup: 'Duplicate', series: 'Series' },
      aiBudgetExhausted: 'The AI assistant did not reach every row.',
      options: {
        title: 'Options',
        create_clients: 'Create the clients that do not exist',
        create_sites: 'Create the addresses that do not exist',
        default_status: 'Status of the imported bookings',
        import_duplicates: 'Import the duplicates too',
        hint: 'A row with a blocking warning is always imported as "To review".',
      },
      commit_one: 'Import {{count}} row',
      commit_other: 'Import {{count}} rows',
      skippedCount_one: '({{count}} skipped)',
      skippedCount_other: '({{count}} skipped)',
      committing: 'Importing {{done}} of {{total}}…',
      startOver: 'Start over',
      columns: {
        row: 'No.',
        state: 'State',
        decision: 'Decision',
        service_date: 'Date',
        start_time: 'Start',
        end_time: 'End',
        client_name: 'Client',
        phone: 'Phone',
        address: 'Address',
        postal_code: 'Postcode',
        city: 'Town',
        service: 'Service',
        title: 'Title',
        notes: 'Notes',
        tags: 'Tags',
        issues: 'Warnings',
        outcome: 'Result',
      },
      seriesNotImported: 'Recurring series — not imported automatically',
      seriesHint: 'The whole series is not imported; create it by hand or import its first date only.',
      importFirstOnly: 'Import the first date only',
      duplicateOf: 'Duplicate of an existing booking',
      duplicateInFile: 'Repeated in this import',
      openBooking: 'Open booking',
      rawText: 'Original text',
      editCell: 'Edit',
      pickClient: 'Pick from my clients',
      unlinkClient: 'Remove the chosen client',
      linkedClient: 'Linked client: {{name}}',
      closePicker: 'Close',
      provenance: {
        label: 'Source',
        from: 'Read from: "{{text}}"',
        original: 'Original: {{value}}',
        edited: 'Edited by hand',
        none: 'Not in the original text',
      },
      confidence: { high: 'Certain reading', low: 'Uncertain reading', ai: 'Suggested by the AI assistant' },
      issues: {
        missing_date: 'Date missing',
        ambiguous_date: 'Ambiguous date',
        invalid_date: 'Invalid date',
        invalid_local_time: 'That time does not exist that day (clock change)',
        past_date: 'Date in the past',
        missing_service: 'Service missing',
        missing_client: 'Client missing',
        unknown_client: 'Client not found in your contacts',
        missing_address: 'Address missing',
        duplicate: 'Duplicate',
        recurring_not_expanded: 'Recurring series not expanded',
        ics_cancel: 'Event cancelled in the source calendar',
        ics_cancel_unknown: 'Cancels an event that was never imported',
        ics_update: 'Updates a booking imported before',
        ics_no_uid: 'Event without an identifier (UID)',
        ics_override: 'Change to one date of a series',
        multi_day: 'Spans several days',
        crosses_midnight: 'Ends after midnight',
        dst_adjusted: 'Time adjusted for the clock change',
        invalid_event: 'Unreadable event',
        empty_row: 'Empty row',
        ai_suggested_service_date: 'Date suggested by the AI assistant',
        ai_suggested_start_time: 'Time suggested by the AI assistant',
        ai_suggested_address: 'Address suggested by the AI assistant',
        ai_suggested_service: 'Service suggested by the AI assistant',
        ai_suggested_client_name: 'Client suggested by the AI assistant',
        other: 'Warning',
      },
      decisions: { import: 'Import', skip: 'Skip', update: 'Update the booking', cancel: 'Cancel the booking', pending: 'Undecided' },
      outcomes: { imported: 'Imported', skipped: 'Skipped', updated: 'Updated', cancelled: 'Cancelled', failed: 'Failed' },
      report: {
        title: 'Import result',
        imported: 'Imported',
        updated: 'Updated',
        cancelled: 'Cancelled',
        skipped: 'Skipped',
        failed: 'Failed',
        review: 'To review',
        series_pending: 'Series still to create by hand',
        pending: 'Not confirmed',
        rows: 'Row by row',
        viewCalendar: 'Open calendar',
        newImport: 'New import',
      },
      sources: { paste: 'Pasted text', txt: 'Text file', csv: 'CSV', xlsx: 'Excel', ics: 'Calendar (ICS)' },
      statuses: { parsed: 'To confirm', committed: 'Confirmed', cancelled: 'Cancelled' },
      historyEmpty: 'No imports yet.',
      historyColumns: { date: 'Date', source: 'Source', file: 'File', rows: 'Rows', status: 'Status', result: 'Result' },
      open: 'Open',
      notFound: 'The import was not found.',
      backToHistory: 'Back to history',
      readOnly: 'Confirmed import: the rows are shown as they ended up.',
    },
    share: {
      title: 'Client links',
      intro: 'Each link opens a portal where the client books and sees their services. Only a fingerprint of the link is stored: the full address is shown once.',
      new: 'New link',
      newTitle: 'New link for a client',
      editTitle: 'Edit link',
      hint: 'The client is picked from your contacts; it is not changed here.',
      empty: 'No links yet.',
      filterClient: 'Client',
      allClients: 'All clients',
      unnamed: 'Unnamed link',
      created: 'Created on {{date}}',
      uses_one: '{{count}} booking of {{max}}',
      uses_other: '{{count}} bookings of {{max}}',
      usesUnlimited_one: '{{count}} booking',
      usesUnlimited_other: '{{count}} bookings',
      state: { active: 'Active', expired: 'Expired', revoked: 'Revoked' },
      columns: { name: 'Link', prefix: 'Code', state: 'Status', uses: 'Use', created: 'Created' },
      fields: {
        client: 'Client',
        name: 'Link name',
        allowBooking: 'Allow bookings',
        allowBookingHint: 'If off, the client only sees their services.',
        autoConfirm: 'Confirm automatically',
        autoConfirmHint: 'Bookings come in confirmed, without your approval.',
        allowHolidays: 'Allow bookings on public holidays',
        allowHolidaysHint: 'When off, the client cannot pick public holidays (Spain national and Catalonia).',
        expires: 'Expires on',
        maxBookings: 'Maximum active bookings',
        daysAhead: 'Days that can be booked ahead',
        slotMinutes: 'Booking slots',
        slotDefault: 'As in the settings',
        wholeDay: 'Whole days',
        dayCapacity: 'Bookings per slot',
        dayCapacityHint: 'Empty = as in the settings.',
        services: 'Services they can choose',
        servicesHint: 'If you pick any, the client must choose one when booking.',
        servicesSearch: 'Search service',
        tags: 'Tags visible to the client',
        tagsHint: 'Only tags marked as visible to the client.',
        message: 'Welcome message',
        lang: 'Portal language',
      },
      minutes: '{{count}} min',
      noProducts: 'There are no products in your catalogue.',
      noTags: 'There are no client-visible tags.',
      langs: { es: 'Español', en: 'English', ca: 'Català' },
      problems: {
        clientRequired: 'Choose the client.',
        daysAhead: 'Days ahead must be between 1 and 365.',
        number: 'Check the numbers: they must be whole numbers from 1 up.',
        message: 'The message can be at most 500 characters.',
      },
      saved: 'Link saved.',
      revoke: 'Revoke',
      revokeTitle: 'Revoke link',
      revokeText: 'The link stops working right away. Existing bookings are kept.',
      revoked: 'Link revoked.',
      regenerate: 'Generate new link',
      regenerateTitle: 'Generate a new link',
      regenerateText: 'The previous address stops working and a new one is created for the same client.',
      tokenTitle: 'Link ready',
      tokenOnce: 'Copy or share the link now: for security it will not be shown again.',
      copy: 'Copy',
      copied: 'Link copied.',
      share: 'Share',
      whatsapp: 'Send via WhatsApp',
      shareTitle: 'Your booking portal',
      shareText: 'Hi {{name}}, with this link you can book and see your services:',
      done: 'Done',
    },
    portal: {
      title: 'Your bookings',
      loading: 'Loading…',
      linkInactive: 'This link is no longer active',
      linkInactiveHint: 'Ask the business for a new link.',
      tooManyRequests: 'Too many requests. Wait a few minutes and try again.',
      loadFailed: 'The portal could not be loaded. Check your connection.',
      greeting: 'Hello, {{name}}',
      language: 'Language',
      tabs: { book: 'Book', services: 'My services' },
      bookingClosed: 'This link does not accept new bookings.',
      bookingsLeft_one: 'You can make {{count}} more booking with this link.',
      bookingsLeft_other: 'You can make {{count}} more bookings with this link.',
      date: 'Date',
      noDates: 'There are no free days in this period.',
      full: 'Fully booked — not available',
      legend: { free: 'Available', partial: 'Partly booked', full: 'Fully booked (not available)' },
      days: 'Days',
      typeDays: 'Type the days (e.g. 1 5 7)',
      addDays: 'Add',
      allMonth: 'Add every free day this month',
      selectedCount_one: '{{count}} day selected',
      selectedCount_other: '{{count}} days selected',
      removeDay: 'Remove {{date}}',
      bookedCount_one: '{{count}} day booked',
      bookedCount_other: '{{count}} days booked',
      skippedNote_one: '{{count}} day unavailable:',
      skippedNote_other: '{{count}} days unavailable:',
      noneBooked: 'None of the chosen days could be booked.',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      time: 'Time',
      noSlots: 'No free times left that day.',
      wholeDay: 'Whole day',
      service: 'Service',
      pickService: 'Choose a service',
      tags: 'Tags',
      what: 'What do you need',
      description: 'Description',
      notes: 'Notes for the business',
      site: 'Service address',
      review: 'Review your booking',
      continue: 'Review',
      edit: 'Edit',
      send: 'Send booking',
      sending: 'Sending…',
      captcha: 'Confirm you are not a robot.',
      newTag: 'New tag',
      newTags: 'New tags',
      addTag: 'Add',
      removeTag: 'Remove {{name}}',
      newTagHint: 'Up to {{max}} tags of at most {{len}} characters.',
      problems: {
        date: 'Choose a day.',
        time: 'Choose a time.',
        service: 'Choose a service.',
        title: 'Say what you need.',
        captcha: 'Tick the “I am not a robot” box.',
      },
      slotFullAlternative: 'There is room at {{time}}.',
      slotFullNone: 'Go back and choose another time.',
      pickAlternative: 'Book at {{time}}',
      sent: 'Booking sent',
      reference: 'Reference',
      pendingNote: 'The business will review your request and confirm the booking.',
      confirmedNote: 'Your booking is confirmed.',
      another: 'Make another booking',
      upcoming: 'Upcoming',
      past: 'Past',
      noBookings: 'You have no services yet.',
      completionNotes: 'Service notes',
      report: 'View delivery report',
      reportLoading: 'Loading report…',
      documentUnavailable: 'The report is not available.',
      download: 'Download PDF',
      downloadFailed: 'The PDF could not be created.',
      previewFailed: 'The preview could not be shown; you can still download the PDF.',
      pageAlt: 'Page {{n}} of {{total}}',
      back: 'Back to my services',
    },
    sites: {
      title: 'Sites',
      new: 'New site',
      newTitle: 'New site',
      editTitle: 'Edit site',
      hint: 'The address where the service happens. The client is picked from your contacts; it is not changed here.',
      search: 'Search site, address or client',
      empty: 'No sites yet.',
      name: 'Name',
      client: 'Client',
      address: 'Address',
      city: 'City',
      contact: 'Contact',
      nameRequired: 'Enter the site name.',
      saved: 'Site saved.',
      archive: 'Archive',
      archiveTitle: 'Archive site',
      archiveText: '"{{name}}" will no longer be offered when creating bookings. Existing bookings keep it.',
      archived: 'Site archived.',
      fields: {
        name: 'Name',
        address: 'Address',
        number: 'Number',
        postal_code: 'Postcode',
        city: 'City',
        province: 'Province',
        country: 'Country',
        contact_name: 'Contact person',
        contact_phone: 'Contact phone',
        lat: 'Latitude',
        lng: 'Longitude',
        access_instructions: 'Access instructions',
        parking_instructions: 'Parking',
        notes: 'Notes',
      },
    },
    tags: {
      title: 'Tags',
      new: 'New tag',
      newTitle: 'New tag',
      editTitle: 'Edit tag',
      hint: 'Visible tags are shown to the client; selectable ones can be chosen by them.',
      empty: 'No tags yet.',
      name: 'Name',
      color: 'Colour',
      clientVisible: 'Visible to the client',
      clientSelectable: 'The client can choose it',
      sourceLabel: 'Source',
      source: { internal: 'Internal', client: 'From the client' },
      yes: 'Yes',
      no: 'No',
      nameRequired: 'Enter the tag name.',
      saved: 'Tag saved.',
      archive: 'Archive',
      archiveTitle: 'Archive tag',
      archiveText: '"{{name}}" will no longer be offered. Bookings that already have it keep it.',
      archived: 'Tag archived.',
    },
    settings: {
      title: 'Book Me settings',
      saved: 'Settings saved.',
      nothingToSave: 'There is nothing to save.',
      hours: 'Hours',
      timezone: 'Time zone',
      slotMinutes: 'Slot size',
      dayStart: 'Opening time',
      dayEnd: 'Closing time',
      rangeInvalid: 'The closing time must be after the opening time.',
      dayCapacity: 'Bookings per slot or day',
      defaultDuration: 'Default duration (min)',
      documents: 'Documents',
      docType: 'Document on completion',
      docMode: 'Generation',
      docStatus: 'Invoice state',
      docTypes: { none: 'None', delivery_note: 'Delivery note', invoice: 'Invoice' },
      docModes: { auto: 'Automatic', manual: 'Manual' },
      docStatuses: { Draft: 'Draft', Sent: 'Sent' },
      taxRate: 'Default VAT (%)',
      irpfRate: 'Withholding tax (%)',
      portal: 'Client portal',
      requiresApproval: 'Client requests need approval',
      requiresApprovalHelp: 'Default for new links; each link can change it',
      shareDocuments: 'Share documents with the client',
      clientTags: 'Clients can create tags',
      notices: 'Notices',
      notifyEmail: 'Email me',
    },
    complete: {
      title: 'Complete service',
      actualAt: 'Actual date and time',
      duration: 'Actual duration (min)',
      notes: 'Completion notes',
      reviewLines: 'Review lines',
      hideLines: 'Hide lines',
      linesHint: 'Changes only affect this booking and the document that gets created.',
      submit: 'Complete',
      policy: {
        delivery_note_auto: 'A draft delivery note will be created',
        invoice_auto: 'A draft invoice will be created',
        none: 'No document will be created',
        manual: 'The document will wait to be generated by hand',
      },
      toast: {
        generated: 'Service completed and document created',
        pending: 'Service completed; the document is waiting to be generated',
        skipped: 'Service completed; no document is created',
        completed: 'Service completed',
      },
    },
    attachments: {
      title: 'Attachments',
      note: 'Attachments are internal: the client never sees them.',
      upload: 'Upload file',
      uploading: 'Uploading…',
      empty: 'No attachments.',
      delete: 'Delete attachment',
      confirmDelete: 'Delete this attachment?',
      badType: 'Only images and PDF files are accepted.',
      uploadFailed: 'The file could not be uploaded.',
    },
    notifications: {
      title: 'Notices',
      aria: 'Notices ({{count}} unread)',
      none: 'No notices.',
      markRead: 'Mark as read',
    },
    errors: {
      document_exists: 'This booking\'s document already exists or is being created.',
      not_completed: 'The service is not completed yet.',
      client_required: 'The booking needs a client to create the document.',
      lines_required: 'The booking needs at least one line to create the document.',
      snapshot_missing: 'The completion has no document policy.',
      reserved_id_missing: 'The reserved document id is missing.',
      invalid_path: 'The file path is not valid.',
      slot_full: 'That time is already fully booked.',
      performer_clash: 'The assignee already has another job at that time.',
      link_limit: 'This link has reached its booking limit.',
      invalid_client: 'Invalid client.',
      invalid_vendor: 'Invalid supplier.',
      invalid_assignee: 'The assignee is not part of your team.',
      invalid_product: 'Invalid product.',
      invalid_site: 'Invalid address.',
      invalid_tag: 'Invalid tag.',
      invalid_time: 'Check the date and times.',
      invalid_days: 'Consecutive days only apply to a whole-day booking with a date (2 to 31).',
      invalid_local_time: 'That time does not exist on that day because of the clock change.',
      invalid_status: 'Invalid status.',
      invalid_transition: 'The booking cannot move to that status from its current one.',
      reason_required: 'Please give a reason.',
      use_complete_endpoint: 'Use “Complete” to close the job.',
      settings_missing: 'Save the Book Me settings first.',
      feature_off: 'Book Me is not enabled on this account.',
      forbidden: 'You do not have permission to do this.',
      not_found: 'Not found.',
      range_too_long: 'The date range is too long.',
      invalid_timezone: 'Invalid time zone.',
      invalid_value: 'Please check the values entered.',
      use_status_endpoint: 'Change the status with its own button.',
      booking_completed: 'A completed booking cannot be moved or reassigned.',
      duplicate_failed: 'No copy could be created.',
      tag_exists: 'A tag with that name already exists.',
      db_error: 'Could not save. Please try again.',
      nothing_to_import: 'Nothing to import was found.',
      import_closed: 'This row is already imported or the import is closed.',
      too_many_rows: 'Too many rows in a single confirmation.',
      too_large: 'The text or file is too large (at most 500,000 characters or 2,000 rows).',
      invalid_decision: 'That decision is not valid for this row.',
      invalid_date: 'Invalid date.',
      internal: 'Unexpected error on this row.',
      duplicate: 'Already imported (duplicate).',
      already_cancelled: 'The booking was already cancelled.',
      pending: 'Undecided row.',
      loadFailed: 'Could not load the data.',
      link_invalid: 'This link is no longer active.',
      past_date: 'That date or time has already passed.',
      outside_window: 'That date is outside the booking period.',
      tags_not_allowed: 'New tags cannot be created.',
      captcha_failed: 'We could not verify that you are not a robot. Please try again.',
      booking_disabled: 'This link does not accept bookings.',
      holiday: 'That day is a public holiday; pick another.',
      rate_limited: 'Too many requests. Wait a few minutes.',
      stale_status: 'The booking changed in the meantime; reload the page',
      unknown: 'Something went wrong. Please try again.',
    },
    billing: {
      title: 'Hourly billing',
      assignedIndicator: 'Billed by the hour (OND)',
      viewInvoice: 'View OND invoice',
      schemes: 'Billing schemes',
      period: 'Period',
      format: 'Format',
      formatDay: 'One line per day',
      formatMonth: 'One line for the whole month',
      preview: 'Preview',
      create: 'Create invoice',
      release: 'Release bookings',
      noBookings: 'No bookings to bill in this period',
      hours: 'Hours',
      rate: 'Hourly rate',
      days: 'Days',
      hoursTotal: 'Total hours',
      daysTotal: 'Total days',
      includePending: 'Include pending bookings',
      created: 'Invoice created',
      invoiceNumber: 'Invoice no.',
      schemeTitle: 'Billing scheme',
      addScheme: 'Add scheme',
      product: 'Product',
      productHint: 'Product or service billed by the hour',
      hoursPerDay: 'Hours per day',
      defaultFormat: 'Default format',
      perBookingDocument: 'One document per booking',
      seriesPrefix: 'Series prefix',
    },
    ond: {
      period: 'OND period',
      totalHours: 'Total hours',
      totalDays: 'Total days',
    },
  },
  perms: {
    mod_bookme: 'Book Me Calendar',
    'p_bookme.view': 'View calendar',
    'p_bookme.manage': 'Create and edit bookings',
    'p_bookme.import': 'Import bookings',
    'p_bookme.assign': 'Assign performer',
    'p_bookme.complete': 'Complete jobs',
    'p_bookme.documents': 'Generate delivery note / invoice',
    'p_bookme.share': 'Client links',
    'p_bookme.settings': 'Book Me settings',
  },
};

const ca = {
  bookme: {
    title: 'Book Me Calendar',
    nav: {
      calendar: 'Calendari',
      import: 'Importar',
      share: 'Enllaços per a clients',
      sites: 'Adreces',
      tags: 'Etiquetes',
      settings: 'Configuració',
    },
    status: {
      imported: 'Importada',
      review: 'Per revisar',
      available: 'Disponible',
      booked: 'Sol·licitada',
      assigned: 'Assignada',
      confirmed: 'Confirmada',
      in_progress: 'En curs',
      completed: 'Completada',
      cancelled: 'Cancel·lada',
    },
    doc: {
      none: 'Sense document',
      pending: 'Document pendent',
      generating: 'Generant el document',
      generated: 'Document creat',
      failed: 'Error en el document',
      skipped: 'Sense document (omès)',
      title: 'Document',
      generate: 'Generar document',
      regenerate: 'Generar de nou',
      deleted: 'Document eliminat',
      deletedHint: 'El document ja no existeix; el pots generar de nou.',
      invoicedAs: 'Facturat →',
      skippedNote: 'Sense document (política: cap)',
      pendingNote: 'El document està pendent de generar.',
      staleHint: 'La generació sembla encallada.',
      failedTitle: 'No s\'ha pogut crear el document',
      readFailed: 'No s\'ha pogut llegir el document.',
      issueHint: 'S\'emet des de la vista del document.',
      type: {
        delivery_note: 'Albarà',
        invoice: 'Factura',
      },
      errors: {
        client_required: 'La reserva necessita un client.',
        lines_required: 'La reserva necessita almenys una línia.',
        snapshot_missing: 'Falta la política de document de la finalització.',
        reserved_id_missing: 'Falta l\'identificador reservat del document.',
      },
      invoiceStatus: {
        Draft: 'Esborrany',
        Sent: 'Emès',
        Paid: 'Cobrat',
        Overdue: 'Vençut',
        Converted: 'Convertit',
      },
    },
    actions: {
      new: 'Nova reserva',
      save: 'Desar',
      cancel: 'Cancel·lar',
      edit: 'Editar',
      duplicate: 'Duplicar',
      delete: 'Eliminar',
      retry: 'Tornar-ho a provar',
      review: 'Enviar a revisió',
      available: 'Marcar com a disponible',
      assigned: 'Assignar',
      confirmed: 'Confirmar',
      in_progress: 'Començar',
      completed: 'Completar',
      cancelled: 'Cancel·lar la reserva',
      reopen: 'Reobrir',
      approve: 'Aprovar',
      reject: 'Rebutjar',
    },
    form: {
      client: 'Client',
      service: 'Servei',
      date: 'Data',
      startTime: 'Hora d\'inici',
      endTime: 'Hora de fi',
      wholeDay: 'Tot el dia',
      duration: 'Durada',
      site: 'Adreça',
      assignee: 'Assignada a',
      status: 'Estat',
      tags: 'Etiquetes',
      internalNotes: 'Notes internes',
      clientNotes: 'Notes per al client',
      reason: 'Motiu',
      customerReference: 'Referència del client',
      title: 'Servei',
      category: 'Categoria',
      performer: 'Executor',
      team: 'Equip',
      suppliers: 'Proveïdors',
      noPerformer: 'Sense assignar',
      newClient: 'Nou client (s\'obre Clients)',
      noSite: 'Sense adreça',
      newSite: 'Nova adreça…',
      clientSites: 'Adreces del client',
      otherSites: 'Altres adreces',
      savedSites: 'Adreces desades',
      lines: 'Línies del servei',
      description: 'Descripció',
      quantity: 'Quant.',
      unit: 'Unitat',
      unitPrice: 'Preu',
      discount: 'Dte. %',
      taxRate: 'Impost %',
      removeLine: 'Treure la línia',
      linesTotal: 'Import orientatiu (sense impostos)',
      noTags: 'No hi ha etiquetes.',
      endsAt: 'Acaba a les {{time}}',
      newTitle: 'Nova reserva',
      editTitle: 'Editar la reserva #{{ref}}',
      titleRequired: 'Indica el servei.',
      dateRequired: 'Indica la data.',
      durationRequired: 'Indica la durada.',
      durationUnit: 'Unitat de durada',
      durationHours: 'Hores',
      durationDays: 'Dies',
      daysInvalid: 'Indica entre 1 i 31 dies.',
      seriesEditHint: 'Les reserves de diversos dies s\'editen dia a dia.',
      seriesNote: 'Es crearà una reserva de dia sencer a cadascun dels {{count}} dies seguits.',
      groupCreated: 'Creades {{count}} reserves',
      groupPartial: 'Creades {{count}} de {{total}}: {{date}} — {{reason}}',
      siteFields: {
        name: 'Nom',
        address: 'Adreça',
        number: 'Número',
        postal_code: 'Codi postal',
        city: 'Població',
        province: 'Província',
        country: 'País',
        contact_name: 'Persona de contacte',
        contact_phone: 'Telèfon de contacte',
        access_instructions: 'Com accedir-hi',
        parking_instructions: 'Aparcament',
      },
    },
    calendar: {
      wholeDay: 'Tot el dia',
      today: 'Avui',
      loading: 'Carregant les reserves…',
      prev: 'Anterior',
      next: 'Següent',
      viewLabel: 'Vista',
      pickMonth: 'Mes',
      pickYear: 'Any',
      newOn: 'Nova reserva el {{date}}',
      views: { month: 'Mes', week: 'Setmana', day: 'Dia', agenda: 'Agenda' },
      more: '+{{count}} més',
      noBookings: 'No hi ha reserves.',
      noClient: 'Sense client',
      deletedClient: 'Client eliminat',
      deletedPerformer: 'Executor eliminat',
      deletedSite: 'Adreça eliminada',
      deletedProduct: 'Producte eliminat',
      reviewPending_one: '{{count}} reserva pendent de revisió',
      reviewPending_other: '{{count}} reserves pendents de revisió',
      filters: {
        title: 'Filtres',
        text: 'Cercar',
        textPlaceholder: 'Cerca per núm., servei o client',
        performer: 'Executor',
        any: 'Tots',
        outOfRange: 'Seleccionat (fora d\'aquestes dates)',
        document: 'Document',
        clear: 'Treure els filtres',
      },
    },
    conflict: {
      alternative: 'Primera hora lliure aquell dia: {{time}}.',
      noAlternative: 'No queda cap hora lliure aquell dia.',
      useAlternative: 'Fer servir les {{time}}',
      clashRefs: 'Coincideix amb: {{refs}}',
      force: 'Forçar',
    },
    detail: {
      notFound: 'No s\'ha trobat la reserva.',
      back: 'Tornar al calendari',
      untitled: 'Reserva sense títol',
      when: 'Data i hora',
      noDate: 'Sense data',
      minutes_one: '{{count}} minut',
      minutes_other: '{{count}} minuts',
      tzHint: 'Hores a la zona {{tz}}.',
      groupSeries: 'Forma part d\'una sèrie de {{count}} dies',
      groupOpen: 'Veure al calendari',
      whatsapp: 'WhatsApp',
      directions: 'Com arribar-hi',
      noLines: 'Sense línies.',
      assign: 'Assignar executor',
      assignTitle: 'Assignar executor',
      notes: 'Notes',
      timeline: 'Historial i comentaris',
      commentPlaceholder: 'Escriu un comentari…',
      sendComment: 'Comentar',
      noEvents: 'Encara no hi ha historial.',
      shareWithClient: 'Compartir amb el client',
      duplicateTitle: 'Duplicar la reserva',
      duplicateDates: 'En dates concretes',
      duplicateWeekly: 'Cada setmana',
      addDate: 'Afegir data',
      weeks: 'Nombre de setmanes (inclosa aquesta)',
      weeksHint: 'Entre 2 i 52; les còpies comencen la setmana següent.',
      duplicated_one: '{{count}} còpia creada',
      duplicated_other: '{{count}} còpies creades',
      duplicateSkipped: 'No s\'han pogut crear: {{dates}}',
    },
    events: {
      created: 'Reserva creada',
      updated: 'Dades modificades',
      rescheduled: 'Canvi de data o d\'hora',
      assigned: 'Assignació',
      status: 'Canvi d\'estat',
      tagged: 'Etiquetes canviades',
      comment: 'Comentari',
      completed: 'Servei completat',
      document_generated: 'Document creat',
      document_failed: 'Error en crear el document',
      cancelled: 'Reserva cancel·lada',
      reopened: 'Reserva reoberta',
      public_booked: 'Sol·licitada pel client',
      document_viewed: 'El client ha vist el document',
      document_regenerate: 'Document regenerat',
      approved: 'Sol·licitud aprovada',
      rejected: 'Sol·licitud rebutjada',
      import_updated: 'Actualitzada per una importació',
      import_cancelled: 'Cancel·lada per una importació',
      attachment_added: 'Adjunt afegit',
      attachment_removed: 'Adjunt eliminat',
      other: 'Canvi',
      forced: '(forçat)',
    },
    import: {
      title: 'Importar reserves',
      history: 'Historial',
      historyTitle: 'Importacions anteriors',
      intro: 'Enganxa missatges de WhatsApp, correus o llistes, o puja un fitxer. Res no es converteix en reserva fins que ho confirmis.',
      steps: { source: 'Origen', preview: 'Revisió', report: 'Resultat' },
      paste: 'Text enganxat',
      pastePlaceholder: 'Enganxa aquí els missatges o la llista de serveis…',
      file: 'Fitxer',
      fileHint: '.txt (xat de WhatsApp exportat), .csv, .xlsx o .ics (invitacions de calendari). El fitxer es llegeix al teu navegador.',
      fileChosen: 'Fitxer: {{name}}',
      fileWins: 'S\'analitzarà el fitxer; el text enganxat s\'ignora mentre n\'hi hagi un d\'escollit.',
      removeFile: 'Treure el fitxer',
      ai: 'Assistent IA',
      aiHint: 'Completa la data, l\'adreça o el servei que faltin amb les paraules del mateix missatge. Només s\'aplica si la plataforma el té activat.',
      analyze: 'Analitzar',
      emptyFile: 'El fitxer no té files.',
      unreadableFile: 'No s\'ha pogut llegir el fitxer.',
      unsupportedFile: 'Tipus de fitxer no admès. Fes servir .txt, .csv, .xlsx o .ics.',
      rowsFound_one: '{{count}} fila analitzada',
      rowsFound_other: '{{count}} files analitzades',
      states: { ok: 'Correcta', warn: 'Amb avisos', blocked: 'Bloquejada', dup: 'Duplicada', series: 'Sèrie' },
      aiBudgetExhausted: 'L\'assistent IA no va arribar a totes les files.',
      options: {
        title: 'Opcions',
        create_clients: 'Crear els clients que no existeixin',
        create_sites: 'Crear les adreces que no existeixin',
        default_status: 'Estat de les reserves importades',
        import_duplicates: 'Importar també els duplicats',
        hint: 'Una fila amb un avís bloquejant s\'importa sempre com a «Per revisar».',
      },
      commit_one: 'Importar {{count}} fila',
      commit_other: 'Importar {{count}} files',
      skippedCount_one: '({{count}} omesa)',
      skippedCount_other: '({{count}} omeses)',
      committing: 'Important {{done}} de {{total}}…',
      startOver: 'Tornar a començar',
      columns: {
        row: 'Núm.',
        state: 'Estat',
        decision: 'Decisió',
        service_date: 'Data',
        start_time: 'Inici',
        end_time: 'Fi',
        client_name: 'Client',
        phone: 'Telèfon',
        address: 'Adreça',
        postal_code: 'C. postal',
        city: 'Població',
        service: 'Servei',
        title: 'Títol',
        notes: 'Notes',
        tags: 'Etiquetes',
        issues: 'Avisos',
        outcome: 'Resultat',
      },
      seriesNotImported: 'Sèrie recurrent — no s\'importa automàticament',
      seriesHint: 'La sèrie completa no s\'importa; crea-la a mà o importa només la primera data.',
      importFirstOnly: 'Importar només la primera data',
      duplicateOf: 'Duplicat d\'una reserva existent',
      duplicateInFile: 'Repetida en aquesta importació',
      openBooking: 'Veure la reserva',
      rawText: 'Text original',
      editCell: 'Editar',
      pickClient: 'Triar dels meus clients',
      unlinkClient: 'Treure el client triat',
      linkedClient: 'Client vinculat: {{name}}',
      closePicker: 'Tancar',
      provenance: {
        label: 'Procedència',
        from: 'Llegit de: «{{text}}»',
        original: 'Original: {{value}}',
        edited: 'Editat a mà',
        none: 'No apareix al text original',
      },
      confidence: { high: 'Lectura segura', low: 'Lectura dubtosa', ai: 'Suggerit per l\'assistent IA' },
      issues: {
        missing_date: 'Falta la data',
        ambiguous_date: 'Data ambigua',
        invalid_date: 'Data no vàlida',
        invalid_local_time: 'Aquesta hora no existeix aquell dia (canvi d\'hora)',
        past_date: 'Data passada',
        missing_service: 'Falta el servei',
        missing_client: 'Falta el client',
        unknown_client: 'Client no trobat als teus contactes',
        missing_address: 'Falta l\'adreça',
        duplicate: 'Duplicada',
        recurring_not_expanded: 'Sèrie recurrent sense expandir',
        ics_cancel: 'Esdeveniment cancel·lat al calendari d\'origen',
        ics_cancel_unknown: 'Cancel·la un esdeveniment que no s\'havia importat',
        ics_update: 'Actualitza una reserva importada abans',
        ics_no_uid: 'Esdeveniment sense identificador (UID)',
        ics_override: 'Canvi d\'una data concreta d\'una sèrie',
        multi_day: 'Dura diversos dies',
        crosses_midnight: 'Acaba passada la mitjanit',
        dst_adjusted: 'Hora ajustada pel canvi d\'hora',
        invalid_event: 'Esdeveniment il·legible',
        empty_row: 'Fila buida',
        ai_suggested_service_date: 'Data suggerida per l\'assistent IA',
        ai_suggested_start_time: 'Hora suggerida per l\'assistent IA',
        ai_suggested_address: 'Adreça suggerida per l\'assistent IA',
        ai_suggested_service: 'Servei suggerit per l\'assistent IA',
        ai_suggested_client_name: 'Client suggerit per l\'assistent IA',
        other: 'Avís',
      },
      decisions: { import: 'Importar', skip: 'Ometre', update: 'Actualitzar la reserva', cancel: 'Cancel·lar la reserva', pending: 'Sense decidir' },
      outcomes: { imported: 'Importada', skipped: 'Omesa', updated: 'Actualitzada', cancelled: 'Cancel·lada', failed: 'Amb error' },
      report: {
        title: 'Resultat de la importació',
        imported: 'Importades',
        updated: 'Actualitzades',
        cancelled: 'Cancel·lades',
        skipped: 'Omeses',
        failed: 'Amb error',
        review: 'Per revisar',
        series_pending: 'Sèries pendents de crear a mà',
        pending: 'Sense confirmar',
        rows: 'Detall per fila',
        viewCalendar: 'Veure el calendari',
        newImport: 'Nova importació',
      },
      sources: { paste: 'Text enganxat', txt: 'Fitxer de text', csv: 'CSV', xlsx: 'Excel', ics: 'Calendari (ICS)' },
      statuses: { parsed: 'Per confirmar', committed: 'Confirmada', cancelled: 'Cancel·lada' },
      historyEmpty: 'Encara no hi ha importacions.',
      historyColumns: { date: 'Data', source: 'Origen', file: 'Fitxer', rows: 'Files', status: 'Estat', result: 'Resultat' },
      open: 'Obrir',
      notFound: 'No s\'ha trobat la importació.',
      backToHistory: 'Tornar a l\'historial',
      readOnly: 'Importació confirmada: les files es mostren tal com van quedar.',
    },
    share: {
      title: 'Enllaços per a clients',
      intro: 'Cada enllaç obre un portal on el client reserva i veu els seus serveis. Només es desa una empremta de l\'enllaç: l\'adreça completa es mostra una sola vegada.',
      new: 'Nou enllaç',
      newTitle: 'Nou enllaç per a un client',
      editTitle: 'Edita l\'enllaç',
      hint: 'El client es tria entre els teus contactes; aquí no es modifica.',
      empty: 'Encara no hi ha enllaços.',
      filterClient: 'Client',
      allClients: 'Tots els clients',
      unnamed: 'Enllaç sense nom',
      created: 'Creat el {{date}}',
      uses_one: '{{count}} reserva de {{max}}',
      uses_other: '{{count}} reserves de {{max}}',
      usesUnlimited_one: '{{count}} reserva',
      usesUnlimited_other: '{{count}} reserves',
      state: { active: 'Actiu', expired: 'Caducat', revoked: 'Revocat' },
      columns: { name: 'Enllaç', prefix: 'Codi', state: 'Estat', uses: 'Ús', created: 'Creat' },
      fields: {
        client: 'Client',
        name: 'Nom de l\'enllaç',
        allowBooking: 'Permet reserves',
        allowBookingHint: 'Si no, el client només veu els seus serveis.',
        autoConfirm: 'Confirma automàticament',
        autoConfirmHint: 'Les reserves entren confirmades, sense la teva aprovació.',
        allowHolidays: 'Permetre reserves en festius',
        allowHolidaysHint: 'Si es desactiva, el client no pot triar dies festius (estatals i de Catalunya).',
        expires: 'Caduca el',
        maxBookings: 'Màxim de reserves actives',
        daysAhead: 'Dies que es poden reservar per avançat',
        slotMinutes: 'Trams de reserva',
        slotDefault: 'Com als ajustos',
        wholeDay: 'Dies sencers',
        dayCapacity: 'Reserves per tram',
        dayCapacityHint: 'Buit = com als ajustos.',
        services: 'Serveis que pot triar',
        servicesHint: 'Si en tries algun, el client n\'haurà d\'escollir un en reservar.',
        servicesSearch: 'Cerca un servei',
        tags: 'Etiquetes visibles per al client',
        tagsHint: 'Només les etiquetes marcades com a visibles per al client.',
        message: 'Missatge de benvinguda',
        lang: 'Idioma del portal',
      },
      minutes: '{{count}} min',
      noProducts: 'No hi ha productes al teu catàleg.',
      noTags: 'No hi ha etiquetes visibles per al client.',
      langs: { es: 'Español', en: 'English', ca: 'Català' },
      problems: {
        clientRequired: 'Tria el client.',
        daysAhead: 'Els dies per avançat han d\'estar entre 1 i 365.',
        number: 'Revisa els números: han de ser enters a partir d\'1.',
        message: 'El missatge admet com a màxim 500 caràcters.',
      },
      saved: 'Enllaç desat.',
      revoke: 'Revoca',
      revokeTitle: 'Revoca l\'enllaç',
      revokeText: 'L\'enllaç deixarà de funcionar a l\'instant. Les reserves fetes es conserven.',
      revoked: 'Enllaç revocat.',
      regenerate: 'Genera un enllaç nou',
      regenerateTitle: 'Genera un enllaç nou',
      regenerateText: 'L\'adreça anterior deixarà de funcionar i se\'n crearà una altra per al mateix client.',
      tokenTitle: 'Enllaç a punt',
      tokenOnce: 'Copia o comparteix l\'enllaç ara: per seguretat no es tornarà a mostrar.',
      copy: 'Copia',
      copied: 'Enllaç copiat.',
      share: 'Comparteix',
      whatsapp: 'Envia per WhatsApp',
      shareTitle: 'El teu portal de reserves',
      shareText: 'Hola {{name}}, des d\'aquest enllaç pots reservar i veure els teus serveis:',
      done: 'Fet',
    },
    portal: {
      title: 'Les teves reserves',
      loading: 'S\'està carregant…',
      linkInactive: 'Aquest enllaç ja no està actiu',
      linkInactiveHint: 'Demana un enllaç nou a l\'empresa.',
      tooManyRequests: 'Massa sol·licituds. Espera uns minuts i torna-ho a provar.',
      loadFailed: 'No s\'ha pogut carregar el portal. Revisa la connexió.',
      greeting: 'Hola, {{name}}',
      language: 'Idioma',
      tabs: { book: 'Reserva', services: 'Els meus serveis' },
      bookingClosed: 'Aquest enllaç no admet reserves noves.',
      bookingsLeft_one: 'Pots fer {{count}} reserva més amb aquest enllaç.',
      bookingsLeft_other: 'Pots fer {{count}} reserves més amb aquest enllaç.',
      date: 'Data',
      noDates: 'No hi ha dies lliures en aquest període.',
      full: 'Complet — no disponible',
      legend: { free: 'Disponible', partial: 'Parcialment reservat', full: 'Complet (no disponible)' },
      days: 'Dies',
      typeDays: 'Escriu els dies (p. ex. 1 5 7)',
      addDays: 'Afegeix',
      allMonth: 'Afegeix tots els dies lliures del mes',
      selectedCount_one: '{{count}} dia triat',
      selectedCount_other: '{{count}} dies triats',
      removeDay: 'Treu {{date}}',
      bookedCount_one: 'Reservat {{count}} dia',
      bookedCount_other: 'Reservats {{count}} dies',
      skippedNote_one: '{{count}} dia no disponible:',
      skippedNote_other: '{{count}} dies no disponibles:',
      noneBooked: 'Cap dels dies triats es pot reservar.',
      prevMonth: 'Mes anterior',
      nextMonth: 'Mes següent',
      time: 'Hora',
      noSlots: 'No queden hores lliures aquell dia.',
      wholeDay: 'Dia sencer',
      service: 'Servei',
      pickService: 'Tria un servei',
      tags: 'Etiquetes',
      what: 'Què necessites',
      description: 'Descripció',
      notes: 'Notes per a l\'empresa',
      site: 'Adreça del servei',
      review: 'Revisa la teva reserva',
      continue: 'Revisa',
      edit: 'Edita',
      send: 'Envia la reserva',
      sending: 'S\'està enviant…',
      captcha: 'Confirma que no ets un robot.',
      newTag: 'Etiqueta nova',
      newTags: 'Etiquetes noves',
      addTag: 'Afegeix',
      removeTag: 'Treu {{name}}',
      newTagHint: 'Fins a {{max}} etiquetes de {{len}} caràcters com a màxim.',
      problems: {
        date: 'Tria un dia.',
        time: 'Tria una hora.',
        service: 'Tria un servei.',
        title: 'Indica què necessites.',
        captcha: 'Marca la casella «No sóc un robot».',
      },
      slotFullAlternative: 'Hi ha lloc a les {{time}}.',
      slotFullNone: 'Torna enrere i tria una altra hora.',
      pickAlternative: 'Reserva a les {{time}}',
      sent: 'Reserva enviada',
      reference: 'Referència',
      pendingNote: 'L\'empresa revisarà la teva sol·licitud i et confirmarà la reserva.',
      confirmedNote: 'La teva reserva està confirmada.',
      another: 'Fes una altra reserva',
      upcoming: 'Propers',
      past: 'Anteriors',
      noBookings: 'Encara no tens serveis.',
      completionNotes: 'Notes del servei',
      report: 'Mostra l\'informe de lliurament',
      reportLoading: 'S\'està carregant l\'informe…',
      documentUnavailable: 'L\'informe no està disponible.',
      download: 'Baixa el PDF',
      downloadFailed: 'No s\'ha pogut crear el PDF.',
      previewFailed: 'No s\'ha pogut mostrar la previsualització; pots baixar el PDF.',
      pageAlt: 'Pàgina {{n}} de {{total}}',
      back: 'Torna als meus serveis',
    },
    sites: {
      title: 'Llocs',
      new: 'Nou lloc',
      newTitle: 'Nou lloc',
      editTitle: 'Edita el lloc',
      hint: 'Adreça on es fa el servei. El client es tria entre els teus contactes; aquí no es modifica.',
      search: 'Cerca lloc, adreça o client',
      empty: 'Encara no hi ha llocs.',
      name: 'Nom',
      client: 'Client',
      address: 'Adreça',
      city: 'Ciutat',
      contact: 'Contacte',
      nameRequired: 'Indica el nom del lloc.',
      saved: 'Lloc desat.',
      archive: 'Arxiva',
      archiveTitle: 'Arxiva el lloc',
      archiveText: '«{{name}}» deixarà d\'oferir-se en crear reserves. Les reserves existents el conserven.',
      archived: 'Lloc arxivat.',
      fields: {
        name: 'Nom',
        address: 'Adreça',
        number: 'Número',
        postal_code: 'Codi postal',
        city: 'Ciutat',
        province: 'Província',
        country: 'País',
        contact_name: 'Persona de contacte',
        contact_phone: 'Telèfon de contacte',
        lat: 'Latitud',
        lng: 'Longitud',
        access_instructions: 'Com accedir',
        parking_instructions: 'Aparcament',
        notes: 'Notes',
      },
    },
    tags: {
      title: 'Etiquetes',
      new: 'Nova etiqueta',
      newTitle: 'Nova etiqueta',
      editTitle: 'Edita l\'etiqueta',
      hint: 'Les etiquetes visibles es mostren al client; les seleccionables les pot triar ell.',
      empty: 'Encara no hi ha etiquetes.',
      name: 'Nom',
      color: 'Color',
      clientVisible: 'Visible per al client',
      clientSelectable: 'El client pot triar-la',
      sourceLabel: 'Origen',
      source: { internal: 'Interna', client: 'Del client' },
      yes: 'Sí',
      no: 'No',
      nameRequired: 'Indica el nom de l\'etiqueta.',
      saved: 'Etiqueta desada.',
      archive: 'Arxiva',
      archiveTitle: 'Arxiva l\'etiqueta',
      archiveText: '«{{name}}» deixarà d\'oferir-se. Les reserves que ja la tenen la conserven.',
      archived: 'Etiqueta arxivada.',
    },
    settings: {
      title: 'Configuració de Book Me',
      saved: 'Configuració desada.',
      nothingToSave: 'No hi ha canvis per desar.',
      hours: 'Horari',
      timezone: 'Zona horària',
      slotMinutes: 'Mida de franja',
      dayStart: 'Hora d\'obertura',
      dayEnd: 'Hora de tancament',
      rangeInvalid: "L'hora de tancament ha de ser posterior a la d'obertura.",
      dayCapacity: 'Reserves per franja o dia',
      defaultDuration: 'Durada per defecte (min)',
      documents: 'Documents',
      docType: 'Document en completar',
      docMode: 'Generació',
      docStatus: 'Estat de la factura',
      docTypes: { none: 'Cap', delivery_note: 'Albarà', invoice: 'Factura' },
      docModes: { auto: 'Automàtica', manual: 'Manual' },
      docStatuses: { Draft: 'Esborrany', Sent: 'Enviada' },
      taxRate: 'IVA per defecte (%)',
      irpfRate: 'IRPF (%)',
      portal: 'Portal del client',
      requiresApproval: 'Les sol·licituds del client requereixen aprovació',
      requiresApprovalHelp: 'Valor per defecte dels enllaços nous; cada enllaç el pot canviar',
      shareDocuments: 'Comparteix documents amb el client',
      clientTags: 'El client pot crear etiquetes',
      notices: 'Avisos',
      notifyEmail: 'Avisa\'m per correu',
    },
    complete: {
      title: 'Completar servei',
      actualAt: 'Data i hora reals',
      duration: 'Durada real (min)',
      notes: 'Notes de finalització',
      reviewLines: 'Revisar línies',
      hideLines: 'Amagar línies',
      linesHint: 'Els canvis només afecten aquesta reserva i el document que es creï.',
      submit: 'Completar',
      policy: {
        delivery_note_auto: 'Es crearà un albarà en esborrany',
        invoice_auto: 'Es crearà una factura en esborrany',
        none: 'No es crearà cap document',
        manual: 'El document quedarà pendent de generar a mà',
      },
      toast: {
        generated: 'Servei completat i document creat',
        pending: 'Servei completat; el document queda pendent de generar',
        skipped: 'Servei completat; no es crea document',
        completed: 'Servei completat',
      },
    },
    attachments: {
      title: 'Adjunts',
      note: 'Els adjunts són interns: el client no els veu mai.',
      upload: 'Pujar fitxer',
      uploading: 'Pujant…',
      empty: 'Sense adjunts.',
      delete: 'Eliminar adjunt',
      confirmDelete: 'Vols eliminar aquest adjunt?',
      badType: 'Només s\'admeten imatges i PDF.',
      uploadFailed: 'No s\'ha pogut pujar el fitxer.',
    },
    notifications: {
      title: 'Avisos',
      aria: 'Avisos ({{count}} sense llegir)',
      none: 'No hi ha avisos.',
      markRead: 'Marcar llegits',
    },
    errors: {
      document_exists: 'El document d\'aquesta reserva ja existeix o s\'està creant.',
      not_completed: 'El servei encara no està completat.',
      client_required: 'La reserva necessita un client per crear el document.',
      lines_required: 'La reserva necessita almenys una línia per crear el document.',
      snapshot_missing: 'Falta la política de document de la finalització.',
      reserved_id_missing: 'Falta l\'identificador reservat del document.',
      invalid_path: 'La ruta del fitxer no és vàlida.',
      slot_full: 'Aquest horari ja és complet.',
      performer_clash: 'La persona assignada ja té un altre servei a aquesta hora.',
      link_limit: 'Aquest enllaç ja ha arribat al seu límit de reserves.',
      invalid_client: 'Client no vàlid.',
      invalid_vendor: 'Proveïdor no vàlid.',
      invalid_assignee: 'La persona assignada no forma part del teu equip.',
      invalid_product: 'Producte no vàlid.',
      invalid_site: 'Adreça no vàlida.',
      invalid_tag: 'Etiqueta no vàlida.',
      invalid_time: 'Revisa la data i les hores.',
      invalid_days: 'Els dies seguits només valen per a una reserva de dia sencer amb data (de 2 a 31).',
      invalid_local_time: 'Aquesta hora no existeix aquell dia pel canvi d\'hora.',
      invalid_status: 'Estat no vàlid.',
      invalid_transition: 'La reserva no pot passar a aquest estat des de l\'actual.',
      reason_required: 'Indica el motiu.',
      use_complete_endpoint: 'Fes servir «Completar» per tancar el servei.',
      settings_missing: 'Desa primer la configuració de Book Me.',
      feature_off: 'Book Me no està activat en aquest compte.',
      forbidden: 'No tens permís per fer això.',
      not_found: 'No s\'ha trobat.',
      range_too_long: 'El període de dates és massa llarg.',
      invalid_timezone: 'Zona horària no vàlida.',
      invalid_value: 'Revisa les dades introduïdes.',
      use_status_endpoint: 'L\'estat es canvia des del seu propi botó.',
      booking_completed: 'Una reserva completada no es pot moure ni reassignar.',
      duplicate_failed: 'No s\'ha pogut crear cap còpia.',
      tag_exists: 'Ja existeix una etiqueta amb aquest nom.',
      db_error: 'No s\'ha pogut desar. Torna-ho a provar.',
      nothing_to_import: 'No s\'ha trobat res per importar.',
      import_closed: 'Aquesta fila ja està importada o la importació està tancada.',
      too_many_rows: 'Massa files en una sola confirmació.',
      too_large: 'El text o el fitxer és massa gran (màxim 500.000 caràcters o 2.000 files).',
      invalid_decision: 'Aquesta decisió no és vàlida per a aquesta fila.',
      invalid_date: 'Data no vàlida.',
      internal: 'Error inesperat en aquesta fila.',
      duplicate: 'Ja estava importada (duplicat).',
      already_cancelled: 'La reserva ja estava cancel·lada.',
      pending: 'Fila sense decidir.',
      loadFailed: 'No s\'han pogut carregar les dades.',
      link_invalid: 'Aquest enllaç ja no està actiu.',
      past_date: 'Aquesta data o hora ja ha passat.',
      outside_window: 'Aquesta data és fora del període de reserves.',
      tags_not_allowed: 'No es poden crear etiquetes noves.',
      captcha_failed: 'No s\'ha pogut verificar que no ets un robot. Torna-ho a provar.',
      booking_disabled: 'Aquest enllaç no admet reserves.',
      holiday: "Aquell dia és festiu; tria'n un altre.",
      rate_limited: 'Massa sol·licituds. Espera uns minuts.',
      stale_status: 'La reserva ha canviat mentrestant; torna a carregar la pàgina',
      unknown: 'Alguna cosa ha fallat. Torna-ho a provar.',
    },
    billing: {
      title: 'Facturació per hores',
      assignedIndicator: 'Es factura per hores (OND)',
      viewInvoice: 'Veure factura OND',
      schemes: 'Esquemes de facturació',
      period: 'Període',
      format: 'Format',
      formatDay: 'Una línia per dia',
      formatMonth: 'Una línia per a tot el mes',
      preview: 'Vista prèvia',
      create: 'Crear factura',
      release: 'Alliberar reserves',
      noBookings: 'No hi ha reserves per facturar en aquest període',
      hours: 'Hores',
      rate: 'Tarifa per hora',
      days: 'Dies',
      hoursTotal: 'Total d’hores',
      daysTotal: 'Total de dies',
      includePending: 'Incloure reserves pendents',
      created: 'Factura creada',
      invoiceNumber: 'Núm. de factura',
      schemeTitle: 'Esquema de facturació',
      addScheme: 'Afegir esquema',
      product: 'Producte',
      productHint: 'Producte o servei que es factura per hores',
      hoursPerDay: 'Hores per dia',
      defaultFormat: 'Format per defecte',
      perBookingDocument: 'Un document per reserva',
      seriesPrefix: 'Prefix de sèrie',
    },
    ond: {
      period: 'Període OND',
      totalHours: 'Hores totals',
      totalDays: 'Dies totals',
    },
  },
  perms: {
    mod_bookme: 'Book Me Calendar',
    'p_bookme.view': 'Veure el calendari',
    'p_bookme.manage': 'Crear i editar reserves',
    'p_bookme.import': 'Importar reserves',
    'p_bookme.assign': 'Assignar executor',
    'p_bookme.complete': 'Completar serveis',
    'p_bookme.documents': 'Generar albarà / factura',
    'p_bookme.share': 'Enllaços per a clients',
    'p_bookme.settings': 'Configuració de Book Me',
  },
};

const bookmeStrings = { es, en, ca };
export default bookmeStrings;
